import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, Send, Sparkles, UserCheck, CornerUpLeft, CheckCircle2, Clock,
  Search, CalendarClock, AlertTriangle, ChevronRight, StickyNote, Phone, Mail,
} from "@/components/icons";
import { useStore } from "@/store/store";
import {
  OwnershipBadge, LifecycleBadge, ChannelBadge, StageBadge,
  BookingStateBadge, SourceTag, IliaAvatar, Chip, StatusChip,
  AttendanceBadge,
} from "@/components/askilia/atoms";
import { EmptyState } from "@/components/askilia/layout-bits";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { money, fmtTime, initials } from "@/lib/format";
import { getPotentialSale, getCommercialPicture, potentialSaleText } from "@/lib/commercial";
import { findAvailableSlots } from "@/lib/scheduling";
import { useI18n } from "@/i18n";

const FILTERS = [
  { id: "operator", labelKey: "inbox.filters.operator" },
  { id: "ilia", labelKey: "inbox.filters.ilia" },
  { id: "resolved", labelKey: "inbox.filters.resolved" },
  { id: "all", labelKey: "inbox.filters.all" },
  { id: "archive", labelKey: "inbox.filters.archive" },
];

function matchFilter(c, f) {
  const archived = Boolean(c.archivedAt);
  if (f === "archive") return archived;
  if (archived) return false;
  switch (f) {
    case "operator": return ["waiting_human", "handoff_requested", "human"].includes(c.lifecycle);
    case "ilia": return ["ilia", "waiting_customer"].includes(c.lifecycle);
    case "resolved": return c.lifecycle === "resolved";
    default: return true;
  }
}

function customerRelationship(customer, t) {
  const isReturning = customer?.first !== true;
  return {
    label: isReturning ? t("inbox.relationship.returning") : t("inbox.relationship.new"),
    tone: isReturning ? "success" : "muted",
  };
}

function relativeTime(value, t, formatNumber) {
  const elapsed = Math.max(0, Date.now() - new Date(value).getTime());
  const minutes = Math.floor(elapsed / 60000);
  if (minutes < 1) return t("operations.time.justNow");
  if (minutes < 60) return t("operations.time.minutesAgo", { count: formatNumber(minutes) });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t("operations.time.hoursAgo", { count: formatNumber(hours) });
  return t("operations.time.daysAgo", { count: formatNumber(Math.floor(hours / 24)) });
}

export default function Inbox() {
  const { state, actions } = useStore();
  const { t, localize, formatNumber } = useI18n();
  const params = useParams();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedFilter = searchParams.get("filter");
  const [filter, setFilter] = useState(() => FILTERS.some((item) => item.id === requestedFilter) ? requestedFilter : "operator");
  const [query, setQuery] = useState("");
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);

  const customerById = useMemo(
    () => new Map(state.customers.map((customer) => [customer.id, customer])),
    [state.customers]
  );

  const list = useMemo(() => {
    return state.conversations
      .filter((c) => matchFilter(c, filter))
      .filter((c) => !showUnreadOnly || c.unread)
      .filter((c) => {
        if (!query) return true;
        const cust = customerById.get(c.customerId);
        return `${cust?.name || ""} ${localize(cust?.name)}`.toLowerCase().includes(query.toLowerCase());
      })
      .sort((a, b) => new Date(b.lastActivity) - new Date(a.lastActivity));
  }, [state.conversations, customerById, filter, localize, query, showUnreadOnly]);

  const toggleArchive = (conversation) => {
    if (conversation.archivedAt) {
      actions.restoreConversation(conversation.id);
      setFilter("all");
    } else {
      actions.archiveConversation(conversation.id);
      setFilter("archive");
    }
    setShowUnreadOnly(false);
  };

  const active = params.id
    ? state.conversations.find((c) => c.id === params.id)
    : null;

  useEffect(() => {
    if (active?.unread) actions.toggleUnread(active.id, false);
  }, [actions, active?.id, active?.unread]);

  const mobileThread = !!params.id;

  return (
    <div data-lab-inbox-root className="flex h-full">
      {/* LEFT: queues + list */}
      <div data-lab-region="queue" className={`flex w-full flex-col border-r border-border bg-surface lg:w-[360px] ${mobileThread ? "hidden lg:flex" : "flex"}`}>
        <div className="border-b border-border p-4">
          <h1 className="mb-3 text-lg font-semibold tracking-tight">{t("inbox.title")}</h1>
          <div className="relative mb-3">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("inbox.searchPlaceholder")}
              aria-label={t("inbox.searchAria")}
              className="h-9 w-full rounded-lg border border-input bg-background pl-9 pr-3 text-sm outline-none focus:border-ring focus:ring-2 focus:ring-ring/25"
            />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {FILTERS.map((f) => {
              const count = state.conversations.filter((c) => matchFilter(c, f.id)).length;
              return (
                <button
                  key={f.id}
                  onClick={() => {
                    setFilter(f.id);
                    if (f.id === "archive") setShowUnreadOnly(false);
                  }}
                  className={
                    "whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium transition-colors " +
                    (filter === f.id ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:text-foreground")
                  }
                >
                  {t(f.labelKey)} <span className="opacity-70">{formatNumber(count)}</span>
                </button>
              );
            })}
          </div>
          <button
            type="button"
            aria-pressed={showUnreadOnly}
            onClick={() => setShowUnreadOnly((current) => !current)}
            className={
              "mt-3 inline-flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors " +
              (showUnreadOnly
                ? "border-primary/30 bg-primary-soft/35 text-foreground"
                : "border-border bg-surface text-muted-foreground hover:text-foreground")
            }
          >
            <span className="h-2 w-2 rounded-full bg-primary" aria-hidden="true" />
            {t("inbox.showUnread")}
            <span className="text-muted-foreground">{formatNumber(state.conversations.filter((c) => c.unread && !c.archivedAt).length)}</span>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto scroll-thin">
          {list.length === 0 ? (
            <div className="p-6"><EmptyState icon={CheckCircle2} title={t("inbox.empty.title")} description={t("inbox.empty.description")} /></div>
          ) : (
            list.map((c) => {
              const cust = customerById.get(c.customerId);
              const potentialSale = getPotentialSale(cust);
              const conversationChannels = c.channels?.length ? c.channels : (c.channel ? [c.channel] : []);
              const last = c.messages[c.messages.length - 1];
              const isActive = active?.id === c.id;
              return (
                <button
                  key={c.id}
                  onClick={() => nav(`/inbox/${c.id}`)}
                  className={
                    "flex w-full items-start gap-3 border-b border-l-2 border-b-border/70 p-3.5 text-left transition-colors " +
                    (isActive
                      ? "border-l-primary bg-muted/45"
                      : c.unread
                        ? "border-l-primary/70 bg-primary-soft/15 hover:bg-primary-soft/25"
                        : "border-l-transparent hover:bg-muted/45")
                  }
                >
                  <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-secondary text-sm font-semibold text-secondary-foreground">
                    {cust ? initials(localize(cust.name)) : "?"}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex min-w-0 items-center gap-2">
                        <p className={"truncate text-sm " + (c.unread ? "font-semibold text-foreground" : "font-medium text-foreground")}>{localize(cust?.name)}</p>
                        {c.unread ? <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-label={t("inbox.unreadAria")} /> : null}
                        {conversationChannels.length ? (
                          <span
                            className="flex shrink-0 items-center -space-x-0.5"
                            aria-label={t("inbox.channelsAria", { channels: conversationChannels.map(localize).join(", ") })}
                          >
                            {conversationChannels.map((channel, index) => (
                              <ChannelBadge
                                key={`${channel}-${index}`}
                                channel={channel}
                                showLabel={false}
                                className="ring-2 ring-surface"
                              />
                            ))}
                          </span>
                        ) : null}
                      </div>
                      <span className="shrink-0 text-[11px] text-muted-foreground">{relativeTime(c.lastActivity, t, formatNumber)}</span>
                    </div>
                    <p className="mt-0.5 truncate text-xs text-muted-foreground">{localize(last?.text)}</p>
                    <div className="mt-2 flex items-end justify-between gap-2">
                      <div className="flex min-w-0 items-center gap-1.5">
                        <LifecycleBadge status={c.lifecycle} />
                      </div>
                      <PotentialSaleCompact sale={potentialSale} />
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </div>

      {/* CENTER + RIGHT */}
      {active ? (
        <Conversation
          key={active.id}
          conv={active}
          archived={Boolean(active.archivedAt)}
          onToggleArchive={() => toggleArchive(active)}
        />
      ) : (
        <div className="hidden flex-1 items-center justify-center lg:flex">
          <EmptyState icon={Sparkles} title={t("inbox.select.title")} description={t("inbox.select.description")} />
        </div>
      )}
    </div>
  );
}

function Conversation({ conv, archived, onToggleArchive }) {
  const { state, actions } = useStore();
  const { t, localize } = useI18n();
  const nav = useNavigate();
  const cust = state.customers.find((c) => c.id === conv.customerId);
  const booking = state.bookings.find((b) => b.id === conv.bookingId);
  const svc = state.services.find((s) => s.id === (booking?.serviceId || conv.serviceId));
  const res = state.resources.find((r) => r.id === (booking?.resourceId || svc?.resource));
  const potentialSale = getPotentialSale(cust);
  const commercialPicture = getCommercialPicture(cust);
  const relationship = customerRelationship(cust, t);
  const availableSlots = useMemo(() => svc ? findAvailableSlots({
    bookings: state.bookings,
    services: state.services,
    duties: state.roomDuties,
    serviceId: svc.id,
    excludeId: booking?.id || null,
    count: 3,
  }) : [], [state.bookings, state.roomDuties, state.services, svc, booking?.id]);

  const [draft, setDraft] = useState("");
  const [typing, setTyping] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    setDraft(conv.lifecycle === "human" && conv.draft ? conv.draft : "");
  }, [conv.id, conv.lifecycle, conv.draft]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [conv.messages.length, typing]);

  const canAdvance = conv.journey === "lead" && conv.lifecycle === "ilia" && conv.scriptStep < (conv.script?.length || 0);

  const advance = () => {
    setTyping(true);
    setTimeout(() => { actions.iliaAdvance(conv.id); setTyping(false); }, 750);
  };

  const send = () => {
    if (!draft.trim()) return;
    actions.sendHuman(conv.id, draft.trim());
    setDraft("");
  };

  return (
    <div className="flex min-w-0 flex-1">
      {/* CENTER */}
      <div data-lab-region="thread" className="flex min-w-0 flex-1 flex-col bg-surface">
        {/* thread header */}
        <div data-lab-region="thread-header" className="flex items-center gap-3 border-b border-border bg-surface px-4 py-3">
          <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => nav("/inbox")}><ArrowLeft className="h-4 w-4" /></Button>
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-secondary text-sm font-semibold text-secondary-foreground">
            {initials(localize(cust.name))}
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-foreground">{localize(cust.name)}</p>
            <div className="mt-0.5 flex items-center gap-1.5">
              {conv.channels.map((ch) => <ChannelBadge key={ch} channel={ch} showLabel={false} />)}
              <span className="text-xs text-muted-foreground">{conv.channels.map(localize).join(" · ")}</span>
              <span className="text-xs text-muted-foreground" aria-hidden="true">·</span>
              <span className="text-xs font-medium text-muted-foreground">{relationship.label}</span>
            </div>
          </div>
          <PotentialSaleHeader sale={potentialSale} />
          <Button variant="ghost" size="sm" className="hidden shrink-0 lg:inline-flex 2xl:hidden" onClick={onToggleArchive}>
            {archived ? t("inbox.actions.restore") : t("inbox.actions.archive")}
          </Button>
          <div className="hidden items-center gap-2 min-[1760px]:flex">
            <OwnershipBadge owner={conv.owner} />
            <LifecycleBadge status={conv.lifecycle} />
          </div>
        </div>

        {/* messages */}
        <div data-lab-region="messages" ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto scroll-thin px-4 py-5">
          {conv.mergePending && (
            <div className="mx-auto flex max-w-md items-center gap-2 rounded-lg border border-border bg-muted/35 px-3 py-2 text-xs text-muted-foreground">
              <AlertTriangle className="h-4 w-4 shrink-0 text-primary" />
              {t("inbox.mergeNotice", { channel: localize(conv.mergePending.channel), reason: localize(conv.mergePending.reason) })}
            </div>
          )}
          {conv.messages.map((m) => (
            <MessageBubble
              key={m.id}
              m={m}
              conv={conv}
              custName={localize(cust.name)}
              services={state.services}
              resources={state.resources}
              onSelectSlot={(messageId, slotId) => actions.selectConversationSlot(conv.id, messageId, slotId)}
              onConfirmSlot={() => actions.confirmConversationSlot(conv.id)}
            />
          ))}
          {typing && (
            <div className="flex items-center gap-2">
              <IliaAvatar size={28} active />
              <div className="flex items-center gap-1 rounded-2xl rounded-bl-sm bg-surface px-3.5 py-3 shadow-xs">
                {[0, 1, 2].map((i) => <span key={i} className="h-1.5 w-1.5 rounded-full bg-muted-foreground/60 ilia-dot" style={{ animationDelay: `${i * 0.15}s` }} />)}
              </div>
            </div>
          )}
        </div>

        {/* action bar / composer */}
        <div data-lab-region="composer" className="border-t border-border bg-surface p-3.5">
          <ActionBar
            conv={conv}
            draft={draft}
            setDraft={setDraft}
            send={send}
            canAdvance={canAdvance}
            advance={advance}
            typing={typing}
            booking={booking}
            service={svc}
            availableSlots={availableSlots}
            potentialSale={potentialSale}
          />
        </div>
      </div>

      {/* RIGHT: context */}
      <aside data-lab-region="context" className="hidden w-[460px] shrink-0 flex-col overflow-y-auto scroll-thin border-l border-border bg-surface 2xl:flex">
        <ContextPanel
          conv={conv}
          cust={cust}
          booking={booking}
          svc={svc}
          res={res}
          commercialPicture={commercialPicture}
          availableSlots={availableSlots}
          archived={archived}
          onToggleArchive={onToggleArchive}
        />
      </aside>
    </div>
  );
}

function MessageBubble({ m, conv, custName, services, resources, onSelectSlot, onConfirmSlot }) {
  const { t, localize, formatDate, formatNumber } = useI18n();
  if (m.from === "system") {
    return (
      <div className="mx-auto flex max-w-md items-center gap-2 rounded-lg border border-border bg-muted/35 px-3 py-2 text-xs text-muted-foreground">
        <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-warning" /> {localize(m.text)}
      </div>
    );
  }
  const isCustomer = m.from === "customer";
  const isIlia = m.from === "ilia";
  const proposalActive = m.bookingProposal && conv.pendingSlot?.id === m.bookingProposal.id;
  return (
    <div className={"flex items-end gap-2 " + (isCustomer ? "" : "flex-row-reverse")}>
      {isIlia ? <IliaAvatar size={28} /> : isCustomer ? (
        <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-secondary text-[11px] font-semibold text-secondary-foreground">{initials(custName)}</span>
      ) : (
        <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-primary text-[11px] font-semibold text-primary-foreground">{initials(localize(m.author) || t("inbox.message.you"))}</span>
      )}
      <div className="max-w-[88%] animate-fade-rise sm:max-w-[78%]">
        <div data-lab-message={isCustomer ? "customer" : isIlia ? "ilia" : "human"} className={
          "rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed " +
          (isCustomer ? "rounded-bl-sm bg-muted/55 text-foreground" : isIlia ? "rounded-br-sm border border-border bg-surface text-foreground" : "rounded-br-sm bg-primary text-primary-foreground")
        }>
          {localize(m.text)}
        </div>
        {m.slotOptions?.length ? (
          <div className="mt-1.5 overflow-hidden rounded-xl border border-border bg-surface shadow-xs">
            <div className="flex items-center justify-between gap-3 border-b border-border px-3 py-2">
              <p className="text-[11px] font-semibold text-muted-foreground">{t("inbox.availability.title")}</p>
              <span className="text-[11px] text-muted-foreground">{t("inbox.availability.choiceCount", { count: formatNumber(m.slotOptions.length) })}</span>
            </div>
            <div className="divide-y divide-border">
              {m.slotOptions.map((slot) => {
                const service = services.find((item) => item.id === (slot.serviceId || m.serviceId));
                const resourceNames = (slot.resourceIds?.length ? slot.resourceIds : [slot.resourceId])
                  .map((id) => resources.find((item) => item.id === id)?.short)
                  .filter(Boolean)
                  .join(" + ");
                const selected = m.selectedSlotId === slot.id;
                const confirmed = m.confirmedSlotId === slot.id;
                return (
                  <button
                    key={slot.id}
                    type="button"
                    disabled={!!m.selectedSlotId}
                    onClick={() => onSelectSlot(m.id, slot.id)}
                    aria-pressed={selected}
                    className={
                      "flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left transition-colors disabled:cursor-default " +
                      (selected ? "bg-primary-soft/55" : "hover:bg-muted/60 disabled:opacity-55")
                    }
                  >
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-foreground">{t("inbox.availability.dateAtTime", { date: formatDate(slot.start, { weekday: "short", month: "short", day: "numeric" }), time: fmtTime(slot.start) })}</span>
                      <span className="block truncate text-[11px] text-muted-foreground">{localize(service?.name)} · {resourceNames.split(" + ").filter(Boolean).map(localize).join(" + ")}</span>
                    </span>
                    {confirmed ? <StatusChip tone="success">{t("inbox.availability.confirmed")}</StatusChip> : selected ? <StatusChip tone="primary">{t("inbox.availability.selected")}</StatusChip> : <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />}
                  </button>
                );
              })}
            </div>
            <p className="border-t border-border px-3 py-2 text-[11px] text-muted-foreground">{t("inbox.availability.selectionNote")}</p>
          </div>
        ) : null}
        {m.bookingProposal ? (
          <div className="mt-1.5 rounded-xl border border-primary/25 bg-primary-soft/25 p-3 shadow-xs">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-[11px] font-semibold text-primary-soft-foreground">{t("inbox.availability.readyToConfirm")}</p>
                <p className="mt-1 text-sm font-semibold text-foreground">{t("inbox.availability.dateAtTime", { date: formatDate(m.bookingProposal.start, { weekday: "short", month: "short", day: "numeric" }), time: fmtTime(m.bookingProposal.start) })}</p>
              </div>
              <CalendarClock className="h-4 w-4 shrink-0 text-primary" />
            </div>
            <Button className="mt-3 w-full" size="sm" disabled={!proposalActive} onClick={onConfirmSlot}>
              <CheckCircle2 className="mr-1.5 h-4 w-4" /> {t("inbox.availability.confirmBooking")}
            </Button>
            <p className="mt-2 text-[11px] text-muted-foreground">{t("inbox.availability.validationNote")}</p>
          </div>
        ) : null}
        <p className={"mt-1 px-1 text-[11px] text-muted-foreground " + (isCustomer ? "text-left" : "text-right")}>
          {isIlia ? "Ilia" : isCustomer ? "" : (localize(m.author) || t("inbox.message.you"))} {isCustomer ? "" : "· "}{fmtTime(m.ts)}
        </p>
      </div>
    </div>
  );
}

function ActionBar({ conv, draft, setDraft, send, canAdvance, advance, typing, booking, service, availableSlots, potentialSale }) {
  const { actions } = useStore();
  const { t, localize, formatNumber } = useI18n();
  const sendTimes = () => actions.sendTimeOptions(conv.id, {
    slots: availableSlots,
    serviceId: service?.id,
    bookingId: booking?.id || null,
  });

  // JOURNEY 1 — let Ilia continue
  if (canAdvance) {
    return (
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <IliaAvatar size={22} active /> {t("inbox.composer.iliaQualifying")}
        </p>
        <div className="flex gap-2">
          <Button variant="outline" className="whitespace-nowrap" onClick={() => actions.takeOver(conv.id)}><UserCheck className="mr-1.5 h-4 w-4" /> {t("inbox.actions.takeOver")}</Button>
          <Button className="whitespace-nowrap" onClick={advance} disabled={typing}><Sparkles className="mr-1.5 h-4 w-4" /> {t("inbox.actions.letIliaContinue")}</Button>
        </div>
      </div>
    );
  }

  // JOURNEY 3 — reschedule tool
  if (conv.journey === "reschedule" && conv.lifecycle !== "resolved") {
    return <RescheduleTool conv={conv} booking={booking} service={service} availableSlots={availableSlots} />;
  }

  // Waiting for human (handoff) — take over first
  if (conv.lifecycle === "waiting_human" || conv.lifecycle === "handoff_requested") {
    return (
      <div className="space-y-2.5">
        <div className="flex flex-col gap-3 rounded-lg border border-warning/35 bg-warning-soft/55 p-3 text-sm text-foreground sm:flex-row sm:items-center">
          <div className="flex min-w-0 flex-1 items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
            <span><strong>{t("inbox.handoff.title")}</strong> {localize(conv.handoff?.reason)}. {t("inbox.handoff.paused")}</span>
          </div>
          <div className="shrink-0 border-t border-border pt-2 sm:border-l sm:border-t-0 sm:pl-3 sm:pt-0 sm:text-right">
            <p className="text-[10px] font-semibold text-muted-foreground">{t("inbox.value.potentialSale")}</p>
            <p className="font-sans text-base font-semibold leading-tight tabular-nums">{potentialSaleText(potentialSale)}</p>
          </div>
        </div>
        <Button className="w-full" onClick={() => actions.takeOver(conv.id)}>
          <UserCheck className="mr-1.5 h-4 w-4" /> {t("inbox.actions.takeOverConversation")}
        </Button>
      </div>
    );
  }

  // Human active — editable draft composer
  if (conv.lifecycle === "human") {
    return (
      <div className="space-y-2">
        {conv.draft && draft === conv.draft && (
          <p className="flex items-center gap-1.5 text-xs text-ilia-soft-foreground">
            <Sparkles className="h-3.5 w-3.5" /> {t("inbox.composer.draftNotice")}
          </p>
        )}
        <Textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={3} placeholder={t("inbox.composer.placeholder")} className="resize-none bg-background" />
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={sendTimes}
              disabled={!service || !availableSlots.length}
              title={!service ? t("inbox.composer.linkServiceTitle") : t("inbox.composer.sendChoicesTitle")}
            >
              <CalendarClock className="mr-1.5 h-4 w-4" /> {t("inbox.composer.sendTimes", { count: formatNumber(availableSlots.length || 3) })}
            </Button>
            <Button variant="ghost" size="sm" className="whitespace-nowrap" onClick={() => actions.returnToIlia(conv.id)}><CornerUpLeft className="mr-1.5 h-4 w-4" /> {t("inbox.actions.returnToIlia")}</Button>
            <Button variant="ghost" size="sm" className="whitespace-nowrap" onClick={() => actions.resolve(conv.id)}><CheckCircle2 className="mr-1.5 h-4 w-4" /> {t("inbox.actions.resolve")}</Button>
          </div>
          <Button className="whitespace-nowrap sm:shrink-0" size="sm" onClick={send} disabled={!draft.trim()}><Send className="mr-1.5 h-4 w-4" /> {t("inbox.actions.sendReply")}</Button>
        </div>
      </div>
    );
  }

  // Ilia handling (non-lead) or resolved / waiting customer
  if (conv.lifecycle === "resolved") {
    return (
      <div className="flex items-center justify-between">
        <p className="flex items-center gap-2 text-sm text-success-soft-foreground"><CheckCircle2 className="h-4 w-4" /> {t("inbox.status.resolved")}</p>
        <Button variant="outline" size="sm" className="whitespace-nowrap" onClick={() => actions.takeOver(conv.id)}>{t("inbox.actions.reopenReply")}</Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
      <p className="flex items-center gap-2 text-sm text-muted-foreground"><IliaAvatar size={22} active /> {t("inbox.composer.iliaHandling")}</p>
      <Button variant="outline" className="whitespace-nowrap" onClick={() => actions.takeOver(conv.id)}><UserCheck className="mr-1.5 h-4 w-4" /> {t("inbox.actions.takeOver")}</Button>
    </div>
  );
}

function RescheduleTool({ conv, booking, service, availableSlots }) {
  const { state, actions } = useStore();
  const { t, localize, formatDate, formatNumber } = useI18n();
  const res = state.resources.find((r) => r.id === booking?.resourceId);

  if (!booking) return <p className="text-sm text-muted-foreground">{t("inbox.reschedule.noBooking")}</p>;

  return (
    <div className="space-y-2.5">
      <div className="flex items-start gap-2 rounded-lg border border-border bg-background p-3 text-sm">
        <CalendarClock className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
        <div>
          <p>{t("inbox.reschedule.current")}: <strong>{t("inbox.availability.dateAtTime", { date: formatDate(booking.start, { weekday: "short", month: "short", day: "numeric" }), time: fmtTime(booking.start) })}</strong></p>
          <p className="mt-0.5 text-xs text-muted-foreground">{localize(service?.name)} · {localize(res?.short)}</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5" aria-label={t("inbox.reschedule.alternativesAria")}>
        {availableSlots.map((slot) => (
          <Chip key={slot.id} tone="success">{formatDate(slot.start, { weekday: "short", month: "short", day: "numeric" })} {fmtTime(slot.start)}</Chip>
        ))}
      </div>
      <Button
        className="w-full"
        disabled={!service || !availableSlots.length}
        onClick={() => actions.sendTimeOptions(conv.id, {
          slots: availableSlots,
          serviceId: service?.id,
          bookingId: booking.id,
          text: t("inbox.reschedule.offerMessage", { service: localize(service?.name) }),
        })}
      >
        <Send className="mr-1.5 h-4 w-4" /> {t("inbox.composer.sendTimes", { count: formatNumber(availableSlots.length || 3) })}
      </Button>
      <p className="text-center text-[11px] text-muted-foreground">{t("inbox.reschedule.unchangedNote")}</p>
    </div>
  );
}

function ContextPanel({ conv, cust, booking, svc, res, commercialPicture, availableSlots, archived, onToggleArchive }) {
  const { state, actions } = useStore();
  const { t, localize, formatNumber, formatDate } = useI18n();
  const nav = useNavigate();
  const [note, setNote] = useState("");

  return (
    <div className="divide-y divide-border">
      {/* customer */}
      <div className="p-5">
        <div className="flex items-center gap-3">
          <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-secondary text-sm font-semibold text-secondary-foreground">{initials(localize(cust.name))}</span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-foreground">{localize(cust.name)}</p>
            <button onClick={() => nav(`/customers/${cust.id}`)} className="text-xs text-primary hover:underline">{t("inbox.context.openOpportunity")} <ChevronRight className="inline h-3 w-3" /></button>
          </div>
          <Button variant="ghost" size="sm" className="shrink-0" onClick={onToggleArchive}>
            {archived ? t("inbox.actions.restore") : t("inbox.actions.archive")}
          </Button>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <StageBadge stage={cust.stage} />
          <StatusChip tone={customerRelationship(cust, t).tone}>{customerRelationship(cust, t).label}</StatusChip>
        </div>
        <CommercialPicture picture={commercialPicture} />
        <div className="mt-3 space-y-1.5 text-xs text-muted-foreground">
          {cust.phone && <p className="flex items-center gap-2"><Phone className="h-3.5 w-3.5" /> {cust.phone}</p>}
          {cust.email && <p className="flex items-center gap-2"><Mail className="h-3.5 w-3.5" /> {cust.email}</p>}
          <p className="flex items-center gap-2"><Clock className="h-3.5 w-3.5" /> {t("inbox.context.historyCounts", { visits: formatNumber(cust.history.visits), noShows: formatNumber(cust.history.noShows), moved: formatNumber(cust.history.moved) })}</p>
        </div>
      </div>

      {/* handoff packet */}
      {conv.handoff && (
        <div className="p-5">
          <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
            <AlertTriangle className="h-3.5 w-3.5" /> {t("inbox.handoff.packet")}
          </p>
          <div className="space-y-2.5 rounded-lg border border-border border-l-2 border-l-warning bg-surface p-3 text-sm">
            <Field label={t("inbox.handoff.customerAsked")} value={localize(conv.handoff.request)} />
            <Field label={t("inbox.handoff.summary")} value={localize(conv.handoff.summary)} />
            <div className="grid grid-cols-2 gap-2">
              <Field label={t("inbox.handoff.intent")} value={localize(conv.handoff.intent)} />
              <Field label={t("inbox.handoff.urgency")} value={localize(conv.handoff.urgency)} />
            </div>
            <Field label={t("inbox.handoff.whyEscalated")} value={localize(conv.handoff.reason)} />
            <div>
              <p className="text-[11px] font-medium text-muted-foreground">{t("inbox.handoff.missing")}</p>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {conv.handoff.missing.map((m) => <StatusChip key={m} tone="warning">{localize(m)}</StatusChip>)}
              </div>
            </div>
            <Field label={t("inbox.handoff.relevantHistory")} value={localize(conv.handoff.history)} />
          </div>
        </div>
      )}

      {svc && availableSlots.length > 0 && conv.lifecycle !== "resolved" ? (
        <QuickAvailability
          conv={conv}
          service={svc}
          resources={state.resources}
          booking={booking}
          slots={availableSlots}
          actions={actions}
        />
      ) : null}

      {/* captured fields */}
      <div className="p-5">
        <p className="mb-2 text-xs font-semibold text-muted-foreground">{t("inbox.context.capturedFields")}</p>
        <div className="space-y-2">
          {conv.captured.map((f, i) => (
            <div key={i} className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-[11px] text-muted-foreground">{localize(f.key)}</p>
                <p className="text-sm font-medium text-foreground">{localize(f.value)}</p>
                {f.key === "Service" && svc?.description ? (
                  <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{localize(svc.description)}</p>
                ) : null}
              </div>
              <SourceTag source={f.source} />
            </div>
          ))}
        </div>
      </div>

      {/* booking context */}
      {booking && (
        <div className="p-5">
          <p className="mb-2 text-xs font-semibold text-muted-foreground">{t("inbox.context.booking")}</p>
          <div className="rounded-lg border border-border bg-background p-3">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-foreground">{localize(svc?.name)}</p>
              <BookingStateBadge state={booking.state} />
            </div>
            {svc?.description ? <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{localize(svc.description)}</p> : null}
            <p className="mt-1 text-xs text-muted-foreground">{localize(res?.short)} · {formatDate(booking.start, { weekday: "short", month: "short", day: "numeric" })} {fmtTime(booking.start)} · {money(booking.price)}</p>
            <div className="mt-2"><AttendanceBadge booking={booking} compact /></div>
            <Button variant="ghost" size="sm" className="mt-2 px-0 text-primary" onClick={() => nav("/calendar")}>{t("inbox.context.openCalendar")} <ChevronRight className="ml-1 h-3.5 w-3.5" /></Button>
          </div>
        </div>
      )}

      {/* quick note */}
      <div className="p-5">
        <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-muted-foreground"><StickyNote className="h-3.5 w-3.5" /> {t("inbox.context.internalNote")}</p>
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder={t("inbox.context.notePlaceholder")} className="resize-none bg-background text-sm" />
        <Button size="sm" variant="outline" className="mt-2 w-full" disabled={!note.trim()} onClick={() => { actions.addNote(cust.id, note.trim()); setNote(""); }}>{t("inbox.context.saveNote")}</Button>
      </div>
    </div>
  );
}

function CommercialPicture({ picture }) {
  const { t, localize } = useI18n();
  return (
    <section className="mt-3 overflow-hidden rounded-lg border border-border border-l-2 border-l-accent bg-surface" aria-label={t("inbox.commercial.currentDealAria", { value: potentialSaleText(picture.potential) })}>
      <div className="p-3">
        <div className="flex items-center justify-between gap-2">
          <p className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-muted-foreground"><span className="h-1.5 w-1.5 rounded-full bg-accent" /> {t("inbox.commercial.currentDeal")}</p>
          <SourceTag source={picture.potential.source} />
        </div>
        <p className="mt-1 font-sans text-2xl font-semibold tracking-tight text-foreground tabular-nums">{potentialSaleText(picture.potential)}</p>
        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{t("inbox.commercial.atStake")} {localize(picture.potential.basis)}</p>
      </div>
      <div className="grid grid-cols-2 gap-px border-t border-border bg-border">
        <div className="bg-surface p-3">
          <div className="flex flex-col items-start gap-1.5">
            <p className="text-[11px] text-muted-foreground">{t("inbox.commercial.spent")}</p>
            <SourceTag source={picture.spentSource} />
          </div>
          <p className="mt-1 font-sans text-base font-semibold text-foreground tabular-nums">{money(picture.spent)}</p>
        </div>
        <div className="bg-surface p-3">
          <div className="flex flex-col items-start gap-1.5">
            <p className="text-[11px] text-muted-foreground">{t("inbox.commercial.stillOpen")}</p>
            <SourceTag source={picture.openSource} />
          </div>
          <p className="mt-1 font-sans text-base font-semibold text-foreground tabular-nums">{money(picture.openValue)}</p>
        </div>
      </div>
      <div className="border-t border-border bg-surface p-3">
        <div className="flex flex-wrap items-start justify-between gap-x-2 gap-y-1.5">
          <p className="text-[11px] font-semibold text-muted-foreground">{t("inbox.commercial.worthOverTime")}</p>
          <SourceTag source={picture.lifetimeSource} />
        </div>
        <p className="mt-1 font-sans text-lg font-semibold text-foreground tabular-nums">
          {picture.lifetimeValue === null ? t("inbox.commercial.notEnoughData") : money(picture.lifetimeValue)}
        </p>
        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{localize(picture.lifetimeBasis)}</p>
      </div>
    </section>
  );
}

function QuickAvailability({ conv, service, resources, booking, slots, actions }) {
  const { t, localize, formatDate, formatNumber } = useI18n();
  const sendSlots = (selectedSlots, text = null) => {
    if (["waiting_human", "handoff_requested"].includes(conv.lifecycle)) actions.takeOver(conv.id);
    actions.sendTimeOptions(conv.id, {
      slots: selectedSlots,
      serviceId: service.id,
      bookingId: booking?.id || null,
      text,
    });
  };

  return (
    <section className="p-5" aria-label={t("inbox.quickAvailability.aria")}>
      <div className="mb-2 flex items-center justify-between gap-2">
        <div>
          <p className="text-xs font-semibold text-muted-foreground">{t("inbox.quickAvailability.title")}</p>
          <p className="mt-0.5 text-xs text-muted-foreground">{t("inbox.quickAvailability.description")}</p>
        </div>
        <SourceTag source="derived" />
      </div>
      <div className="space-y-1.5">
        {slots.map((slot) => {
          const label = t("inbox.availability.dateAtTime", { date: formatDate(slot.start, { weekday: "short", month: "short", day: "numeric" }), time: fmtTime(slot.start) });
          const doctor = resources.find((item) => item.id === slot.doctorId);
          const room = resources.find((item) => item.id === slot.roomId);
          const assignmentLabel = [doctor?.short, room?.short].filter(Boolean).map(localize).join(" · ");
          return (
            <Button
              key={slot.id}
              variant="outline"
              size="sm"
              className="h-auto min-h-11 w-full justify-between py-2.5"
              onClick={() => sendSlots([slot], t("inbox.quickAvailability.offerMessage", {
                service: localize(service.name),
                doctor: doctor?.short ? t("inbox.quickAvailability.withDoctor", { doctor: localize(doctor.short) }) : "",
                room: room?.short ? t("inbox.quickAvailability.inRoom", { room: localize(room.short) }) : "",
                time: label,
              }))}
            >
              <span className="min-w-0 text-left">
                <span className="block">{t("inbox.quickAvailability.offer", { time: label })}</span>
                {assignmentLabel ? <span className="block truncate text-[10px] font-normal text-muted-foreground">{assignmentLabel}</span> : null}
              </span>
              <Send className="h-3.5 w-3.5" />
            </Button>
          );
        })}
      </div>
      <Button className="mt-2 w-full" size="sm" onClick={() => sendSlots(slots)}>
        <CalendarClock className="mr-1.5 h-4 w-4" /> {t("inbox.quickAvailability.sendAll", { count: formatNumber(slots.length) })}
      </Button>
    </section>
  );
}

function PotentialSaleCompact({ sale }) {
  const { t } = useI18n();
  return (
    <span
      className="inline-flex shrink-0 items-center gap-1.5 text-right"
      aria-label={t("inbox.value.potentialDealAmountAria", { value: potentialSaleText(sale) })}
      title={t("inbox.value.potentialDealAmount")}
    >
      <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-accent" aria-hidden="true" />
      <span className="font-sans text-sm font-semibold leading-none text-foreground tabular-nums">
        {potentialSaleText(sale)}
      </span>
    </span>
  );
}

function PotentialSaleHeader({ sale }) {
  const { t } = useI18n();
  return (
    <div className="shrink-0 rounded-lg border border-border bg-surface px-2.5 py-1.5 text-right shadow-xs" aria-label={t("inbox.value.potentialSaleAria", { value: potentialSaleText(sale) })}>
      <p className="inline-flex items-center justify-end gap-1 text-[9px] font-semibold text-muted-foreground"><span className="h-1.5 w-1.5 rounded-full bg-accent" /> {t("inbox.value.potentialSale")}</p>
      <p className="font-sans text-sm font-semibold leading-tight text-foreground tabular-nums">{potentialSaleText(sale)}</p>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <p className="text-[11px] font-medium text-muted-foreground">{label}</p>
      <p className="text-sm text-foreground">{value}</p>
    </div>
  );
}
